import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { handleApiError } from '@/lib/utils'
import { 
  Activity,
  Filter, 
  RefreshCw,
  Search,
  Calendar,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  User,
  UserCheck,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Star,
  Eye,
  Briefcase,
  CalendarPlus,
  Download,
  Settings
} from 'lucide-react'
import { apiClient } from '@/lib/api'
import { Link } from 'react-router-dom'

interface ActivityItem {
  id: string
  type: 'application' | 'interview_completed' | 'candidate_shortlisted' | 'position_update' | 'position_created' | 'interview_scheduled'
  message: string
  timestamp: string
  candidateId?: string
  positionId?: string
  candidateName?: string
  positionTitle?: string
  priority: 'low' | 'medium' | 'high' | 'urgent'
}

interface ActivityResponseData {
  activityFeed: ActivityItem[]
  pagination: {
    currentPage: number
    totalPages: number
    totalCount: number
    limit: number
    hasNext: boolean
    hasPrev: boolean
  }
}

export default function ActivityPage() {
  const [activities, setActivities] = useState<ActivityItem[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [sortBy, setSortBy] = useState('newest')
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const itemsPerPage = 20

  const filterOptions = [
    { value: 'all', label: 'All Activities', count: 0 },
    { value: 'position_created', label: 'Positions Created', count: 0 },
    { value: 'interview_scheduled', label: 'Interviews Scheduled', count: 0 },
    { value: 'application', label: 'Applications', count: 0 },
    { value: 'candidate_shortlisted', label: 'Shortlisted', count: 0 },
    { value: 'interview_completed', label: 'Interviews Completed', count: 0 }
  ]

  const fetchActivities = async (page = 1) => {
    try {
      setLoading(true)
      
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        limit: itemsPerPage.toString()
      })
      
      if (filter !== 'all') {
        params.append('activity_type', filter)
      }
      
      if (searchTerm) {
        params.append('search', searchTerm)
      }
      
      // Use the dedicated paginated activity endpoint
      const response = await apiClient.get<ActivityResponseData>(`/dashboard/activity?${params.toString()}`)

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch activities')
      }

      const { activityFeed, pagination } = response.data
      
      // Apply client-side sorting if needed
      let sortedActivities = [...activityFeed]
      if (sortBy === 'oldest') {
        sortedActivities.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
      }
      // Default is already newest first from the API
      
      setActivities(sortedActivities)
      setTotalCount(pagination.totalCount)
      setTotalPages(pagination.totalPages)
      setCurrentPage(pagination.currentPage)
    } catch (error) {
      console.error('Failed to fetch activities:', handleApiError(error))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchActivities(1)
  }, [filter, sortBy, searchTerm])

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      fetchActivities(page)
    }
  }

  const getActivityIcon = (type: string, priority: string) => {
    const iconClass = `w-5 h-5 ${getPriorityColor(priority)}`
    
    switch (type) {
      case 'application':
        return <User className={iconClass} />
      case 'interview_completed':
        return <Calendar className={iconClass} />
      case 'interview_scheduled':
        return <CalendarPlus className={iconClass} />
      case 'candidate_shortlisted':
        return <UserCheck className={iconClass} />
      case 'position_update':
        return <TrendingUp className={iconClass} />
      case 'position_created':
        return <Briefcase className={iconClass} />
      default:
        return <Clock className={iconClass} />
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'text-red-600'
      case 'high':
        return 'text-orange-600'
      case 'medium':
        return 'text-blue-600'
      case 'low':
        return 'text-gray-600'
      default:
        return 'text-gray-600'
    }
  }

  const getPriorityBorder = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'border-l-red-500'
      case 'high':
        return 'border-l-orange-500'
      case 'medium':
        return 'border-l-blue-500'
      case 'low':
        return 'border-l-gray-500'
      default:
        return 'border-l-gray-500'
    }
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    })
  }

  const getActivityTypeLabel = (type: string) => {
    switch (type) {
      case 'position_created': return 'Position Created'
      case 'interview_scheduled': return 'Interview Scheduled'
      case 'application': return 'Application'
      case 'candidate_shortlisted': return 'Candidate Shortlisted'
      case 'interview_completed': return 'Interview Completed'
      case 'position_update': return 'Position Updated'
      default: return 'Activity'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Activity className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">All Activity</h1>
                <p className="text-gray-600 mt-1">Track all your recruitment activities in one place</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={() => fetchActivities(currentPage)}
                disabled={loading}
                className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              
              <button className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow p-6 space-y-6">
              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search Activities</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by message, candidate, position..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Activity Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Activity Type</label>
                <div className="space-y-2">
                  {filterOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setFilter(option.value)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                        filter === option.value
                          ? 'bg-blue-100 text-blue-800 font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sort Options */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                </select>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Results Header */}
            <div className="bg-white rounded-lg shadow mb-6 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500">
                    Showing {activities.length > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0} - {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} activities
                  </span>
                  {filter !== 'all' && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {getActivityTypeLabel(filter)}
                    </span>
                  )}
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-1 rounded-md text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    
                    <span className="text-sm text-gray-700">
                      Page {currentPage} of {totalPages}
                    </span>
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-1 rounded-md text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Activity List */}
            <div className="space-y-4">
              {loading ? (
                <div className="bg-white rounded-lg shadow p-8 text-center">
                  <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
                  <p className="text-gray-600">Loading activities...</p>
                </div>
              ) : activities.length > 0 ? (
                activities.map((activity, index) => (
                  <motion.div
                    key={activity.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`bg-white rounded-lg shadow border-l-4 ${getPriorityBorder(activity.priority)} p-6 hover:shadow-md transition-shadow`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        <div className="flex-shrink-0 mt-1">
                          {getActivityIcon(activity.type, activity.priority)}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {getActivityTypeLabel(activity.type)}
                            </span>
                            {activity.priority === 'high' || activity.priority === 'urgent' ? (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                activity.priority === 'urgent' ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'
                              }`}>
                                {activity.priority === 'urgent' ? 'Urgent' : 'High Priority'}
                              </span>
                            ) : null}
                          </div>
                          
                          <p className="text-gray-900 font-medium mb-2">{activity.message}</p>
                          
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <div className="flex items-center space-x-1">
                              <Clock className="w-4 h-4" />
                              <span>{formatTimestamp(activity.timestamp)}</span>
                            </div>
                            
                            {activity.candidateName && (
                              <div className="flex items-center space-x-1">
                                <User className="w-4 h-4" />
                                <span>{activity.candidateName}</span>
                              </div>
                            )}
                            
                            {activity.positionTitle && (
                              <div className="flex items-center space-x-1">
                                <Briefcase className="w-4 h-4" />
                                <span>{activity.positionTitle}</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Action Links */}
                          <div className="flex items-center space-x-4 mt-3">
                            {activity.positionId && (
                              <Link
                                to={`/recruiter/positions/${activity.positionId}`}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                              >
                                View Position →
                              </Link>
                            )}
                            
                            {activity.candidateId && (
                              <Link
                                to={`/recruiter/candidates/${activity.candidateId}`}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                              >
                                View Candidate →
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="bg-white rounded-lg shadow p-12 text-center">
                  <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No activities found</h3>
                  <p className="text-gray-600 mb-6">
                    {filter !== 'all' 
                      ? `No ${getActivityTypeLabel(filter).toLowerCase()} activities match your current filters.`
                      : 'No activities match your current filters.'
                    }
                  </p>
                  <button
                    onClick={() => {
                      setFilter('all')
                      setSearchTerm('')
                      setSortBy('newest')
                    }}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Clear all filters
                  </button>
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white rounded-lg shadow mt-6 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700">
                      Page {currentPage} of {totalPages} • {totalCount} total activities
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                      className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      First
                    </button>
                    
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    
                    {/* Page numbers */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i
                      if (pageNum > totalPages) return null
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-3 py-2 text-sm font-medium rounded-md ${
                            currentPage === pageNum
                              ? 'text-white bg-blue-600 border border-blue-600'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      )
                    })}
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                    
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Last
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
